import type { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Building, Receipt, Scale, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { formatCurrencyShort } from "@/lib/data";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, NoMoneyAccess, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  fyRange, money, pct, th, td, useCanSeeMoney, useRange, useReport, type ExportSpec,
} from "../ReportKit";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Month {
  month: string;
  month_name: string;
  events: number;
  upcoming_events: number;
  revenue: number;
  gst: number;
  event_costs: number;
  gross_profit: number;
  overheads: number;
  cancelled: number;
  net_profit: number;
  margin: number;
  last_year: { events: number; revenue: number; net_profit: number } | null;
}

interface Totals {
  events: number;
  upcoming_events: number;
  cancelled_events: number;
  revenue: number;
  gst: number;
  event_costs: number;
  gross_profit: number;
  gross_margin: number;
  overheads: number;
  cancelled: number;
  net_profit: number;
  margin: number;
}

interface ProfitLoss {
  period: { from: string; to: string };
  previous_period: { from: string; to: string };
  monthly: Month[];
  totals: Totals;
  previous_totals: Totals;
  by_event_type: { type: string; events: number; guests: number; revenue: number; cost: number; profit: number; margin: number; avg_revenue: number }[];
  overheads_by_category: { id: number | null; name: string; total: number; count: number }[];
}

const signed = (n: number) => (n < 0 ? `− ${money(Math.abs(n))}` : money(n));
const profitClass = (n: number) => (n >= 0 ? "text-emerald-700" : "text-red-600");

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ProfitLossPage() {
  const canSeeMoney = useCanSeeMoney();
  const range = useRange(fyRange(0));
  const { from, to } = range.applied;

  const state = useReport<ProfitLoss>(
    async () => (await reportsApi.profitLoss({ from_date: from, to_date: to })).data,
    [from, to],
    canSeeMoney,
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={Scale}
          iconClass="text-emerald-600"
          title="Profit & Loss"
          subtitle="What each month earned, by when the events took place"
        >
          {canSeeMoney && (
            <HeaderTools>
              <RangeControls range={range} />
              <ExportButtons build={() => state.data && buildExport(state.data)} disabled={state.loading || !state.data} />
            </HeaderTools>
          )}
        </ReportHeader>

        {!canSeeMoney ? <NoMoneyAccess /> : (
          <ReportBody state={state}>
            {(d) => <ProfitLossView d={d} />}
          </ReportBody>
        )}
      </div>
    </DashboardLayout>
  );
}

function ProfitLossView({ d }: { d: ProfitLoss }) {
  const t = d.totals;
  const p = d.previous_totals;
  const isProfit = t.net_profit >= 0;
  const showCancelled = d.monthly.some((m) => m.cancelled !== 0);
  const hasLastYear = p.events > 0 || p.overheads > 0 || p.cancelled !== 0;

  const chart = d.monthly.map((m) => ({
    month: m.month_name.replace(/ (\d{2})(\d{2})$/, " '$2"),
    revenue: m.revenue,
    costs: m.event_costs + m.overheads,
    net_profit: m.net_profit,
    last_year: m.last_year?.net_profit ?? 0,
  }));
  const labels: Record<string, string> = {
    revenue: "Revenue",
    costs: "Event costs + overheads",
    net_profit: "Net profit",
    last_year: "Net profit last year",
  };

  return (
    <>
      <KpiGrid>
        <Kpi label="Revenue" icon={Wallet} tone="emerald" value={money(t.revenue)}
          sub={<Change now={t.revenue} before={p.revenue} show={hasLastYear} fallback={`${t.events} event${t.events === 1 ? "" : "s"}, before GST`} />} />
        <Kpi label="Event costs" icon={Receipt} tone="amber" value={money(t.event_costs)} sub="Vendors, services and event spending" />
        <Kpi label="Overheads" icon={Building} tone="violet" value={money(t.overheads)} sub="Spending not tied to an event" />
        <Kpi
          label={isProfit ? "Net profit" : "Net loss"} icon={isProfit ? TrendingUp : TrendingDown} tone={isProfit ? "blue" : "red"}
          value={money(Math.abs(t.net_profit))} valueClass={isProfit ? "text-blue-800" : "text-red-700"}
          sub={<Change now={t.net_profit} before={p.net_profit} show={hasLastYear} fallback={`${pct(t.margin)} of revenue`} />}
        />
      </KpiGrid>

      <div className="grid gap-4 lg:grid-cols-5">
        {/* The whole period as a statement, top to bottom */}
        <div className="lg:col-span-2">
          <Card title="Statement" subtitle={hasLastYear ? "With the same dates last year" : undefined}>
            <dl className="text-sm">
              <StatementRow label="Revenue (before GST)" value={t.revenue} last={hasLastYear ? p.revenue : undefined} />
              <StatementRow label="Event costs" value={-t.event_costs} last={hasLastYear ? -p.event_costs : undefined} minus />
              <StatementRow label="Gross profit" value={t.gross_profit} last={hasLastYear ? p.gross_profit : undefined} strong note={`${pct(t.gross_margin)} of revenue`} />
              <StatementRow label="Overheads" value={-t.overheads} last={hasLastYear ? -p.overheads : undefined} minus />
              {(t.cancelled !== 0 || p.cancelled !== 0) && (
                <StatementRow label="Cancelled bookings" value={t.cancelled} last={hasLastYear ? p.cancelled : undefined}
                  note={`${t.cancelled_events} cancelled: advance kept − money already spent`} />
              )}
              <StatementRow label={isProfit ? "Net profit" : "Net loss"} value={t.net_profit} last={hasLastYear ? p.net_profit : undefined} strong total note={`${pct(t.margin)} of revenue`} />
            </dl>
            <p className="text-xs text-stone-500 mt-3">
              GST collected on these events: <span className="font-semibold tabular-nums">{money(t.gst)}</span>. It belongs to the government and is not counted above.
            </p>
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card title="Month by month">
            {d.monthly.every((m) => m.revenue === 0 && m.overheads === 0 && m.cancelled === 0) ? (
              <p className="text-sm text-stone-400 py-10 text-center">No events or spending in these dates</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chart} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrencyShort(Number(v))} width={56} />
                  <Tooltip formatter={(v: any, name: any) => [money(v), labels[name] ?? name]} />
                  <Legend formatter={(v) => labels[v] ?? v} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="costs" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="net_profit" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  {hasLastYear && <Line type="monotone" dataKey="last_year" stroke="#a8a29e" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      </div>

      <Card title="Monthly breakdown" subtitle="Net profit = revenue − event costs − overheads">
        {/* Phones: a compact card per month */}
        <div className="md:hidden divide-y divide-stone-100">
          {d.monthly.map((m) => {
            const quiet = m.events === 0 && m.overheads === 0 && m.cancelled === 0;
            return (
              <div key={m.month} className="py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-semibold text-stone-800">
                    {m.month_name}
                    {m.upcoming_events > 0 && <span className="ml-2 text-[10px] font-medium bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded-full">upcoming</span>}
                  </p>
                  <p className={`font-bold tabular-nums ${quiet ? "text-stone-400" : profitClass(m.net_profit)}`}>{quiet ? "—" : signed(m.net_profit)}</p>
                </div>
                {quiet ? (
                  <p className="text-xs text-stone-400">No events or spending</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 mt-1.5 text-xs">
                    <Mini label={`Revenue · ${m.events} ev`} value={money(m.revenue)} />
                    <Mini label="Event costs" value={money(m.event_costs)} />
                    <Mini label="Overheads" value={money(m.overheads)} />
                  </div>
                )}
                {m.last_year && (m.last_year.revenue !== 0 || m.last_year.net_profit !== 0) && (
                  <p className="text-[11px] text-stone-500 mt-1">Last year: {money(m.last_year.revenue)} revenue, {signed(m.last_year.net_profit)} net</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="hidden md:block">
          <TableScroll minWidth={showCancelled ? 980 : 880}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className={`${th} text-left`}>Month</th>
                  <th className={`${th} text-right`}>Events</th>
                  <th className={`${th} text-right`}>Revenue</th>
                  <th className={`${th} text-right`}>Event costs</th>
                  <th className={`${th} text-right`}>Gross profit</th>
                  <th className={`${th} text-right`}>Overheads</th>
                  {showCancelled && <th className={`${th} text-right`}>Cancelled</th>}
                  <th className={`${th} text-right`}>Net profit</th>
                  <th className={`${th} text-right`}>Margin</th>
                  {hasLastYear && <th className={`${th} text-right`}>Net last year</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {d.monthly.map((m) => (
                  <tr key={m.month}>
                    <td className={`${td} font-medium text-stone-700`}>
                      {m.month_name}
                      {m.upcoming_events > 0 && <span className="ml-2 text-[10px] bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded-full" title="Includes events still to come; figures may change">upcoming</span>}
                    </td>
                    <td className={`${td} text-right text-stone-600`}>{m.events}</td>
                    <td className={`${td} text-right tabular-nums text-stone-800`}>{money(m.revenue)}</td>
                    <td className={`${td} text-right tabular-nums text-stone-600`}>{money(m.event_costs)}</td>
                    <td className={`${td} text-right tabular-nums ${profitClass(m.gross_profit)}`}>{money(m.gross_profit)}</td>
                    <td className={`${td} text-right tabular-nums text-stone-600`}>{money(m.overheads)}</td>
                    {showCancelled && <td className={`${td} text-right tabular-nums text-stone-600`}>{money(m.cancelled)}</td>}
                    <td className={`${td} text-right tabular-nums font-semibold ${profitClass(m.net_profit)}`}>{money(m.net_profit)}</td>
                    <td className={`${td} text-right text-stone-500`}>{m.revenue > 0 ? pct(m.margin) : "—"}</td>
                    {hasLastYear && <td className={`${td} text-right tabular-nums text-stone-500`}>{m.last_year ? money(m.last_year.net_profit) : "—"}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                  <td className={`${td} text-stone-800`}>Total</td>
                  <td className={`${td} text-right`}>{t.events}</td>
                  <td className={`${td} text-right tabular-nums`}>{money(t.revenue)}</td>
                  <td className={`${td} text-right tabular-nums`}>{money(t.event_costs)}</td>
                  <td className={`${td} text-right tabular-nums ${profitClass(t.gross_profit)}`}>{money(t.gross_profit)}</td>
                  <td className={`${td} text-right tabular-nums`}>{money(t.overheads)}</td>
                  {showCancelled && <td className={`${td} text-right tabular-nums`}>{money(t.cancelled)}</td>}
                  <td className={`${td} text-right tabular-nums ${profitClass(t.net_profit)}`}>{money(t.net_profit)}</td>
                  <td className={`${td} text-right`}>{t.revenue > 0 ? pct(t.margin) : "—"}</td>
                  {hasLastYear && <td className={`${td} text-right tabular-nums text-stone-600`}>{money(p.net_profit)}</td>}
                </tr>
              </tfoot>
            </table>
          </TableScroll>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <Card title="By event type" subtitle="Which kinds of events earn the most">
            {d.by_event_type.length === 0 ? (
              <p className="text-sm text-stone-400 py-6 text-center">No events in these dates</p>
            ) : (
              <TableScroll minWidth={620}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100">
                      <th className={`${th} text-left`}>Type</th>
                      <th className={`${th} text-right`}>Events</th>
                      <th className={`${th} text-right`}>Revenue</th>
                      <th className={`${th} text-right`}>Avg per event</th>
                      <th className={`${th} text-right`}>Profit</th>
                      <th className={`${th} text-right`}>Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {d.by_event_type.map((r) => (
                      <tr key={r.type}>
                        <td className={`${td} font-medium text-stone-700`}>{r.type}</td>
                        <td className={`${td} text-right text-stone-600`}>{r.events}</td>
                        <td className={`${td} text-right tabular-nums`}>{money(r.revenue)}</td>
                        <td className={`${td} text-right tabular-nums text-stone-600`}>{money(r.avg_revenue)}</td>
                        <td className={`${td} text-right tabular-nums font-semibold ${profitClass(r.profit)}`}>{money(r.profit)}</td>
                        <td className={`${td} text-right text-stone-500`}>{pct(r.margin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card title="Overheads" subtitle="Spending not tied to any event">
            {d.overheads_by_category.length === 0 ? (
              <p className="text-sm text-stone-400 py-6 text-center">No overheads in these dates</p>
            ) : (
              <ul className="space-y-2.5">
                {d.overheads_by_category.map((c) => (
                  <li key={c.id ?? c.name}>
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="text-stone-700 truncate">{c.name}</span>
                      <span className="tabular-nums font-semibold text-stone-800">{money(c.total)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-stone-100 mt-1 overflow-hidden">
                      <div className="h-full bg-violet-400 rounded-full" style={{ width: `${t.overheads > 0 ? (c.total / t.overheads) * 100 : 0}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <Note>
        Revenue is after discount and before GST, counted in the month the event starts. Event costs are what each event costs
        (vendors, services and spending linked to the event). Overheads are spending not linked to any event, on the day it was spent.
        Draft events are not counted. Months marked “upcoming” include events still to come, so their figures can change.
        For money actually received and paid out, see Revenue Trends.
      </Note>
    </>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

/** "▲ 12% vs last year" (or the fallback text when there is nothing to compare with). */
function Change({ now, before, show, fallback }: { now: number; before: number; show: boolean; fallback: ReactNode }) {
  if (!show || before === 0) return <>{fallback}</>;
  const change = ((now - before) / Math.abs(before)) * 100;
  const up = change >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 ${up ? "text-emerald-700" : "text-red-600"}`}>
      <Icon className="w-3.5 h-3.5" />
      {Math.abs(change).toFixed(0)}% vs last year
    </span>
  );
}

function StatementRow({ label, value, last, note, minus = false, strong = false, total = false }: {
  label: string; value: number; last?: number; note?: string; minus?: boolean; strong?: boolean; total?: boolean;
}) {
  return (
    <div className={`flex items-baseline justify-between gap-3 py-2 ${total ? "border-t-2 border-stone-300 mt-1" : strong ? "border-t border-stone-200" : ""}`}>
      <dt className={`${strong ? "font-semibold text-stone-800" : "text-stone-600"} ${minus ? "pl-3" : ""}`}>
        {label}
        {note && <span className="block text-[11px] font-normal text-stone-500">{note}</span>}
      </dt>
      <dd className="text-right">
        <span className={`tabular-nums ${strong ? `font-bold ${profitClass(value)}` : "text-stone-800"}`}>{signed(value)}</span>
        {last !== undefined && <span className="block text-[11px] text-stone-400 tabular-nums">last year {signed(last)}</span>}
      </dd>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-stone-500 truncate">{label}</p>
      <p className="font-semibold tabular-nums text-stone-700 truncate">{value}</p>
    </div>
  );
}

// ─── Excel / Print ───────────────────────────────────────────────────────────

function buildExport(d: ProfitLoss): ExportSpec {
  const t = d.totals;
  const p = d.previous_totals;
  return {
    title: "Profit & Loss",
    fileName: "profit-and-loss",
    period: d.period,
    summary: [
      { label: "Revenue (before GST)", value: t.revenue, type: "money" },
      { label: "Event costs", value: t.event_costs, type: "money" },
      { label: "Gross profit", value: t.gross_profit, type: "money" },
      { label: "Overheads", value: t.overheads, type: "money" },
      { label: "Cancelled bookings (advance kept − money spent)", value: t.cancelled, type: "money" },
      { label: "Net profit", value: t.net_profit, type: "money" },
      { label: "Net margin", value: t.margin, type: "percent" },
      { label: "Events", value: t.events, type: "number" },
      { label: "GST collected (not income)", value: t.gst, type: "money" },
      { label: "Revenue, same dates last year", value: p.revenue, type: "money" },
      { label: "Net profit, same dates last year", value: p.net_profit, type: "money" },
    ],
    tables: [
      {
        title: "Monthly",
        columns: [
          { label: "Month" }, { label: "Events", type: "number" }, { label: "Revenue", type: "money" },
          { label: "Event costs", type: "money" }, { label: "Gross profit", type: "money" }, { label: "Overheads", type: "money" },
          { label: "Cancelled bookings", type: "money" }, { label: "Net profit", type: "money" }, { label: "Margin", type: "percent" },
          { label: "Revenue last year", type: "money" }, { label: "Net profit last year", type: "money" },
        ],
        rows: d.monthly.map((m) => [
          m.month_name, m.events, m.revenue, m.event_costs, m.gross_profit, m.overheads, m.cancelled, m.net_profit, m.margin,
          m.last_year?.revenue ?? null, m.last_year?.net_profit ?? null,
        ]),
        totals: ["Total", t.events, t.revenue, t.event_costs, t.gross_profit, t.overheads, t.cancelled, t.net_profit, t.margin, p.revenue, p.net_profit],
      },
      {
        title: "By event type",
        columns: [
          { label: "Type" }, { label: "Events", type: "number" }, { label: "Guests", type: "number" }, { label: "Revenue", type: "money" },
          { label: "Costs", type: "money" }, { label: "Profit", type: "money" }, { label: "Margin", type: "percent" }, { label: "Avg per event", type: "money" },
        ],
        rows: d.by_event_type.map((r) => [r.type, r.events, r.guests, r.revenue, r.cost, r.profit, r.margin, r.avg_revenue]),
      },
      {
        title: "Overheads",
        columns: [{ label: "Category" }, { label: "Entries", type: "number" }, { label: "Amount", type: "money" }],
        rows: d.overheads_by_category.map((c) => [c.name, c.count, c.total]),
        totals: ["Total", d.overheads_by_category.reduce((a, c) => a + c.count, 0), t.overheads],
      },
    ],
    notes: [
      "Revenue is after discount and before GST, in the month the event starts. Event costs = vendors, services and spending linked to the event.",
      "Overheads = spending not linked to any event, on the day it was spent. Draft events are not counted.",
    ],
  };
}
